import { useMemo } from 'react';

import type { JiraAdminAgentActionPlanPayload } from '@atlassian/ai-mate-jira-admin-agent-api/jira-admin-agent';

import { collapseSingleStepPlan } from '../common/collapseSingleStepPlan';
import type { ConvertedPlan, PlanNode, StepNode } from '../common/types';

export const useConvertActionsToPlan = ({
	actions = [],
}: Partial<Pick<JiraAdminAgentActionPlanPayload, 'actions'>>): ConvertedPlan => {
	return useMemo(() => {
		if (actions.some((action) => action.data.validated === false)) {
			return {};
		}

		const planNodesById = new Map<string, PlanNode>();

		for (const action of actions) {
			if (action.data.actionType === 'plan') {
				const { parentId: _parentId, ...rest } = action.data;
				const planNode: PlanNode = { ...rest, plans: [], steps: [] };
				if (planNode.id !== undefined) {
					planNodesById.set(planNode.id, planNode);
				}
			}
		}

		let topLevelPlan: PlanNode | undefined;

		for (const action of actions) {
			if (action.data.actionType === 'plan') {
				const planNode =
					action.data.id !== undefined ? planNodesById.get(action.data.id) : undefined;
				if (!planNode) {
					continue;
				}

				if (!action.data.parentId) {
					topLevelPlan = topLevelPlan ?? planNode;
				} else {
					planNodesById.get(action.data.parentId)?.plans.push(planNode);
				}
			} else if (action.data.actionType === 'step') {
				if (!action.data.parentId) {
					// for now, we are ignoring a single step being the entire plan - we can address this later
					continue;
				}
				const { parentId: _parentId, ...rest } = action.data;
				const stepNode: StepNode = {
					...rest,
					invocationId: action.invocationId,
				};
				planNodesById.get(action.data.parentId)?.steps.push(stepNode);
			}
		}

		return { topLevelPlan: collapseSingleStepPlan(topLevelPlan) };
	}, [actions]);
};
